"use client";

import { useEffect, useRef } from "react";
import {
  buildContentAnalyticsPayload,
  flushContentImpressions,
  getAnalyticsToken,
  queueContentImpression,
  sendContentView,
} from "@/services/mediaAnalyticsService";

const CONTENT_VIEW_DEDUPE_MS = 2 * 60 * 1000;
const recentContentViews = new Map();

const shouldSendContentView = (payload) => {
  const key = `${payload.mode}:${payload.surface || ""}:${payload.contentKey}`;
  const now = Date.now();
  const lastSentAt = recentContentViews.get(key) || 0;

  if (now - lastSentAt < CONTENT_VIEW_DEDUPE_MS) return false;

  recentContentViews.set(key, now);
  return true;
};

export const useContentViewAnalytics = (content, options = {}) => {
  const sentKeyRef = useRef("");

  useEffect(() => {
    if (options.enabled === false) return;

    const payload = buildContentAnalyticsPayload(content, {
      surface: options.surface || "content_detail",
      source:
        options.source ||
        (typeof document !== "undefined" && document.referrer ? "referrer" : "direct"),
    });

    if (!payload?.contentKey) return;

    const key = `${payload.contentKey}:${payload.mode}`;
    if (sentKeyRef.current === key) return;
    sentKeyRef.current = key;
    if (!shouldSendContentView(payload)) return;

    sendContentView(payload);
  }, [content?.slug, content?.contentKey, content?.isLive, content?.contentType?.kind, options.enabled, options.surface, options.source]);
};

export const useContentImpressionAnalytics = ({
  content,
  surface,
  source,
  containerKey,
  position,
  enabled = true,
  minimumVisibleMs = 0,
  requireAuth = false,
}) => {
  const elementRef = useRef(null);
  const hasSentRef = useRef(false);
  const sentIdentityRef = useRef("");

  useEffect(() => {
    const identity = JSON.stringify([
      content?.slug || content?.contentKey,
      surface,
      source,
      containerKey,
      position,
    ]);
    if (sentIdentityRef.current !== identity) {
      hasSentRef.current = false;
      sentIdentityRef.current = identity;
    }
    if (
      !enabled || hasSentRef.current || typeof IntersectionObserver === "undefined"
    ) return;
    if (requireAuth && !getAnalyticsToken()) return;

    const payload = buildContentAnalyticsPayload(content, {
      surface: surface || "content_list",
      source: source || "content_card",
      containerKey,
      position,
    });

    if (!payload?.contentKey) return;

    const element = elementRef.current;
    if (!element) return;

    let timer;
    let visible = false;
    const clearTimer = () => {
      clearTimeout(timer);
      timer = undefined;
    };
    const sendImpression = () => {
      clearTimer();
      if (
        !visible || document.visibilityState === "hidden" || hasSentRef.current
      ) return;
      if (requireAuth && !getAnalyticsToken()) return;
      hasSentRef.current = true;
      queueContentImpression({ ...payload, visibleMs: minimumVisibleMs || 1000 });
      observer.disconnect();
    };
    const updateTimer = () => {
      clearTimer();
      if (
        !visible || document.visibilityState === "hidden" || hasSentRef.current
      ) return;
      if (minimumVisibleMs > 0) timer = setTimeout(sendImpression, minimumVisibleMs);
      else sendImpression();
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        updateTimer();
      },
      { threshold: [0, 0.5] }
    );

    observer.observe(element);
    document.addEventListener("visibilitychange", updateTimer);
    return () => {
      clearTimer();
      observer.disconnect();
      document.removeEventListener("visibilitychange", updateTimer);
    };
  }, [
    enabled,
    content?.slug,
    content?.contentKey,
    content?.isLive,
    content?.contentType?.kind,
    content?.kind,
    surface,
    source,
    containerKey,
    position,
    minimumVisibleMs,
    requireAuth,
  ]);

  return elementRef;
};

export const useContentImpressionFlush = () => {
  useEffect(() => {
    const interval = setInterval(flushContentImpressions, 15000);

    const flushOnExit = () => {
      flushContentImpressions();
    };

    const flushOnHidden = () => {
      if (document.visibilityState === "hidden") flushContentImpressions();
    };

    window.addEventListener("pagehide", flushOnExit);
    window.addEventListener("beforeunload", flushOnExit);
    document.addEventListener("visibilitychange", flushOnHidden);

    return () => {
      clearInterval(interval);
      flushContentImpressions();
      window.removeEventListener("pagehide", flushOnExit);
      window.removeEventListener("beforeunload", flushOnExit);
      document.removeEventListener("visibilitychange", flushOnHidden);
    };
  }, []);
};
